import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFamilyData } from './useFamilyData';
import { formatDateTime } from './familyDashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import EventIcon from '@mui/icons-material/Event';
import CommandTile from './CommandTile';
import ChoreTile from './ChoreTile';

const FamilyCommandCenter = ({ onNavigateToGrocery, onNavigateToHousehold }) => {
    const navigate = useNavigate();
    const { familyData, loading } = useFamilyData();
    const [dateTime, setDateTime] = useState(formatDateTime());

    // Update time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            setDateTime(formatDateTime());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleAvatarClick = (userId) => {
        // Navigate directly to personal chores for this user
        navigate(`/chores?user=${userId}`);
    };

    // Feature tiles configuration
    const featureTiles = [
        // Column 1
        [
            {
                id: 'grocery',
                icon: ShoppingCartIcon,
                title: 'Grocery Request List',
                bgcolor: '#ed8936',
                hoverColor: '#dd6b20',
                onClick: onNavigateToGrocery
            },
            {
                id: 'household',
                icon: CleaningServicesIcon,
                title: 'Household Supplies',
                bgcolor: '#3182ce',
                hoverColor: '#2c5282',
                onClick: onNavigateToHousehold
            }
        ],
        // Column 2
        [
            {
                id: 'meal-planning',
                icon: RestaurantMenuIcon,
                title: 'Meal Planning',
                bgcolor: '#48bb78',
                hoverColor: '#38a169',
                onClick: () => navigate('/meal-planning')
            },
            {
                id: 'travel',
                icon: AirplanemodeActiveIcon,
                title: 'Travel Log',
                bgcolor: '#0bc5ea',
                hoverColor: '#0987a0',
                onClick: () => navigate('/travel')
            }
        ],
        // Column 3
        [
            {
                id: 'agenda',
                icon: CalendarTodayIcon,
                title: "Today's Agenda",
                bgcolor: '#9f7aea',
                hoverColor: '#805ad5',
                onClick: () => navigate('/agenda')
            },
            {
                id: 'calendar',
                icon: EventIcon,
                title: "Family Calendar",
                bgcolor: '#d53f8c',
                hoverColor: '#b83280',
                onClick: () => navigate('/calendar')
            }
        ]
    ];

    // Drag to scroll logic
    const scrollContainerRef = React.useRef(null);
    const isDown = React.useRef(false);
    const startX = React.useRef(0);
    const scrollLeft = React.useRef(0);
    const [isDragging, setIsDragging] = React.useState(false);

    const handleMouseDown = (e) => {
        isDown.current = true;
        startX.current = e.pageX - scrollContainerRef.current.offsetLeft;
        scrollLeft.current = scrollContainerRef.current.scrollLeft;
    };

    const handleMouseLeave = () => {
        isDown.current = false;
        if (isDragging) {
            setIsDragging(false);
        }
    };

    const handleMouseUp = () => {
        isDown.current = false;
        if (isDragging) {
            // Short timeout to ensure the click event is blocked before re-enabling pointer events
            setTimeout(() => setIsDragging(false), 0);
        }
    };

    const handleMouseMove = (e) => {
        if (!isDown.current) return;
        e.preventDefault();
        
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX.current) * 2; // Scroll-fast
        
        // Only start dragging if moved more than 5px
        if (!isDragging && Math.abs(x - startX.current) > 5) {
            setIsDragging(true);
        }
        
        // Scroll regardless of isDragging state to feel responsive, 
        // but the pointer-events: none blocking only happens when isDragging is true
        if (isDown.current) {
            scrollContainerRef.current.scrollLeft = scrollLeft.current - walk;
        }
    };

    // Prevent click on child elements when dragging
    const handleCaptureClick = (e) => {
        if (isDragging) {
            e.stopPropagation();
        }
    };

    if (loading) {
        return (
            <Box sx={{ 
                height: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                bgcolor: '#f7fafc'
            }}>
                <CircularProgress size={60} thickness={4} sx={{ color: '#4299e1' }} />
            </Box>
        );
    }

    // Get current date and time
    // The original code already has a dateTime state, so we'll use that.
    // const dateTime = formatDateTime(new Date()); 

    return (
        <Box sx={{ 
            height: '100vh', 
            width: '100vw',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#f7fafc'
        }}>
            {/* Header Section */}
            <Box sx={{ 
                p: { xs: 3, md: 5 }, 
                pb: { xs: 2, md: 3 },
                textAlign: 'center'
            }}>
                <Typography 
                    variant="h1" 
                    fontWeight="900" 
                    sx={{ 
                        color: '#2d3748',
                        mb: 1,
                        fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' }
                    }}
                >
                    Dymock Family Central
                </Typography>
                <Typography 
                    variant="h4" 
                    sx={{ 
                        color: '#718096',
                        fontWeight: 500
                    }}
                >
                    {dateTime.date}
                </Typography>
                <Typography 
                    variant="h2" 
                    sx={{ 
                        mt: 1,
                        color: '#4299e1',
                        fontWeight: 700,
                        fontSize: { xs: '2.5rem', md: '3.5rem' }
                    }}
                >
                    {dateTime.time}
                </Typography>
            </Box>

            {/* Main Content - Horizontal Layout */}
            <Box 
                ref={scrollContainerRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                sx={{ 
                    flex: 1,
                    display: 'flex',
                    gap: 3,
                    px: 4,
                    pt: 3,
                    pb: 5,
                    overflowX: 'auto',
                    overflowY: 'visible',
                    minHeight: 0,
                    // Enable smooth scrolling and snap behavior
                    scrollBehavior: isDragging ? 'auto' : 'smooth', // Disable smooth scroll during drag
                    WebkitOverflowScrolling: 'touch',
                    scrollSnapType: isDragging ? 'none' : 'x mandatory', // Disable snap during drag
                    // Hide scrollbar but keep functionality
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    // Cursor styles for drag
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none' // Prevent text selection
                }}
            >
                {/* Chores Section - Avatar Selection Grid */}
                <Box sx={{ 
                    flexShrink: 0,
                    width: { xs: '100%', md: '600px', lg: '700px' },
                    scrollSnapAlign: 'start',
                    pointerEvents: isDragging ? 'none' : 'auto' // Prevent clicks while dragging
                }}>
                    <Grid container spacing={2} sx={{ height: '100%', overflow: 'visible' }}>
                        {familyData.map(user => (
                            <Grid item xs={6} sm={4} md={4} key={user.id}>
                                <ChoreTile user={user} onClick={handleAvatarClick} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Feature Tiles Row (Grouped columns) */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: 3,
                    flexShrink: 0,
                    overflow: 'visible',
                    scrollSnapAlign: 'start',
                    pointerEvents: isDragging ? 'none' : 'auto' // Prevent clicks while dragging
                }}>
                    {featureTiles.map((column, colIndex) => (
                        <Box 
                            key={`col-${colIndex}`} 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 3, 
                                width: { xs: '300px', md: '350px' },
                                height: '100%' 
                            }}
                        >
                            {column.map((tile) => (
                                <Box key={tile.id} sx={{ height: 'calc(50% - 12px)', width: '100%' }}>
                                    <CommandTile
                                        icon={tile.icon}
                                        title={tile.title}
                                        bgcolor={tile.bgcolor}
                                        hoverColor={tile.hoverColor}
                                        onClick={tile.onClick}
                                        sx={{ 
                                            width: '100% !important', 
                                            height: '100% !important'
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default FamilyCommandCenter;
